import React, { useContext } from "react";
import type { TipTapDoc, TipTapMark, TipTapNode } from "../../schema/tiptapDoc";
import { parseLinkHref } from "../../tiptap/link";
import {
    type ProseComponents,
    type ProseRenderConfig,
    ProseRenderConfigContext,
    defaultProseComponents,
} from "./config";

export {
    type ProseComponents,
    type ProseRenderConfig,
    type LinkComponent,
    type ImageComponent,
    type MediaEmbedComponent,
    type MediaEmbedKind,
    type HeadingLevel,
    ProseRenderConfigContext,
    ProseRenderConfigProvider,
    defaultProseComponents,
} from "./config";

export interface TipTapRendererProps {
    doc: TipTapDoc;
    className?: string;
    style?: React.CSSProperties;
    /** Per-call-site overrides. Take precedence over any provider-supplied
     *  components, which themselves override the bare-HTML defaults. */
    components?: ProseRenderConfig;
}

function renderText(node: TipTapNode & { type: "text" }, components: ProseComponents, key: number) {
    let result: React.ReactNode = node.text;
    // Marks wrap from outermost (at the end of the array) to innermost (at the
    // start). Apply in reverse so the outermost ends up wrapping the innermost.
    const marks = node.marks ?? [];
    for (let i = marks.length - 1; i >= 0; i--) {
        result = renderMark(marks[i], result, components);
    }
    return <React.Fragment key={key}>{result}</React.Fragment>;
}

function renderMark(mark: TipTapMark, children: React.ReactNode, components: ProseComponents) {
    switch (mark.type) {
        case "bold":
            return <components.Bold>{children}</components.Bold>;
        case "italic":
            return <components.Italic>{children}</components.Italic>;
        case "underline":
            return <components.Underline>{children}</components.Underline>;
        case "strike":
            return <components.Strike>{children}</components.Strike>;
        case "code":
            return <components.Code>{children}</components.Code>;
        case "link":
            return (
                <components.Link
                    href={mark.attrs.href}
                    parsed={parseLinkHref(mark.attrs.href)}
                    title={mark.attrs.title}
                >
                    {children}
                </components.Link>
            );
    }
}

function renderChildren(
    nodes: TipTapNode[] | undefined,
    components: ProseComponents,
): React.ReactNode[] {
    if (!nodes) return [];
    return nodes.map((child, i) => renderNode(child, components, i));
}

function renderNode(node: TipTapNode, components: ProseComponents, key: number): React.ReactNode {
    switch (node.type) {
        case "text":
            return renderText(node, components, key);
        case "hardBreak":
            return <components.HardBreak key={key} />;
        case "paragraph":
            return (
                <components.Paragraph key={key}>
                    {renderChildren(node.content, components)}
                </components.Paragraph>
            );
        case "heading":
            return (
                <components.Heading key={key} level={node.attrs.level}>
                    {renderChildren(node.content, components)}
                </components.Heading>
            );
        case "blockquote":
            return (
                <components.Blockquote key={key}>
                    {renderChildren(node.content, components)}
                </components.Blockquote>
            );
        case "bulletList":
            return (
                <components.BulletList key={key}>
                    {renderChildren(node.content, components)}
                </components.BulletList>
            );
        case "orderedList":
            return (
                <components.OrderedList key={key} start={node.attrs?.start}>
                    {renderChildren(node.content, components)}
                </components.OrderedList>
            );
        case "listItem":
            return (
                <components.ListItem key={key}>
                    {renderChildren(node.content, components)}
                </components.ListItem>
            );
        case "horizontalRule":
            return <components.HorizontalRule key={key} />;
        case "table": {
            // Split rows into a header section and a body section so the
            // rendered markup matches what react-markdown produces — that's the
            // shape downstream Table overrides (e.g. MarkdownTable's
            // child-walking parser) expect when plugged in. A row counts as a
            // header row when every cell in it is a tableHeader; mixed rows
            // fall through to the body.
            const rows = node.content.filter(
                (r): r is TipTapNode & { type: "tableRow" } => r.type === "tableRow",
            );
            const headerRows = rows.filter(
                (r) => r.content.length > 0 && r.content.every((c) => c.type === "tableHeader"),
            );
            const bodyRows = rows.filter((r) => !headerRows.includes(r));
            return (
                <components.Table key={key}>
                    {headerRows.length > 0 && (
                        <components.TableHead>
                            {headerRows.map((row, i) => renderNode(row, components, i))}
                        </components.TableHead>
                    )}
                    {bodyRows.length > 0 && (
                        <components.TableBody>
                            {bodyRows.map((row, i) => renderNode(row, components, i))}
                        </components.TableBody>
                    )}
                </components.Table>
            );
        }
        case "tableRow":
            return (
                <components.TableRow key={key}>
                    {renderChildren(node.content, components)}
                </components.TableRow>
            );
        case "tableCell":
            return (
                <components.TableCell
                    key={key}
                    colSpan={node.attrs?.colspan}
                    rowSpan={node.attrs?.rowspan}
                >
                    {renderChildren(node.content, components)}
                </components.TableCell>
            );
        case "tableHeader":
            return (
                <components.TableHeader
                    key={key}
                    colSpan={node.attrs?.colspan}
                    rowSpan={node.attrs?.rowspan}
                >
                    {renderChildren(node.content, components)}
                </components.TableHeader>
            );
        case "image":
            return (
                <components.Image
                    key={key}
                    src={node.attrs.src}
                    alt={node.attrs.alt}
                    title={node.attrs.title}
                />
            );
        case "mediaEmbed":
            return (
                <components.MediaEmbed
                    key={key}
                    kind={node.attrs.kind}
                    url={node.attrs.url}
                    variant={node.attrs.variant}
                />
            );
        case "customBlock":
            return (
                <components.CustomBlock
                    key={key}
                    kind={node.attrs.kind}
                    version={node.attrs.version}
                >
                    {renderChildren(node.content, components)}
                </components.CustomBlock>
            );
    }
}

export function TipTapRenderer({
    doc,
    className,
    style,
    components: propOverrides,
}: TipTapRendererProps) {
    const ctxConfig = useContext(ProseRenderConfigContext);
    const components: ProseComponents = {
        ...defaultProseComponents,
        ...(ctxConfig ?? {}),
        ...(propOverrides ?? {}),
    };
    return (
        <div className={className} style={style}>
            {renderChildren(doc.content, components)}
        </div>
    );
}
